import {useMemo, useState} from 'react'
import {MenuProvider, MenuButton} from '@ariakit/react'
import {Button} from '@components/ui/button/Button.tsx'
import {
    DotsHorizontalIcon,
    Download01Icon,
    PencilLineIcon,
    RefreshCw05Icon,
    Trash01Icon
} from '@components/ui/icon/Icon.tsx'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {generatePath, Link} from 'react-router-dom'
import {useTranslation} from '@/translations/i18n.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {useGenerateMedicalReportPdf} from '@/features/medical-reports/queries/useGenerateMedicalReportPdf'
import {toast} from '@/utilities/toast/toast'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {MedicalRecord} from '@/features/medical-records/types'
import dayjs from '@/dayjs'
import {hasPassed5Minutes} from '@/features/medical-records/utils'
import {DeleteMedicalReportModal} from '@/features/medical-reports/components/delete-medical-report-modal/DeleteMedicalReportModal'

export const RowMenu = ({
    patientId,
    medicalReport,
    openDetailsModal
}: {
    patientId: string
    medicalReport: MedicalRecord['reports'][number]
    openDetailsModal: () => void
}) => {
    const {t} = useTranslation()
    const [isOpen, setOpen] = useState(false)
    const [isDeleteReportModalOpen, setIsDeleteReportModalOpen] = useState(false)

    const {mutate: generateMedicalReportPdf, isPending} = useGenerateMedicalReportPdf(
        {medicalReportId: medicalReport.id},
        {
            onMutate: () => {
                toast.loading(t('medical_records:generating_pdf'), {id: 'generate-medical-report-pdf-loading'})
            },
            onSuccess: () => {
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENT_MEDICAL_RECORD, {patientId}]})
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MEDICAL_REPORTS, {medicalReportId: medicalReport.id}]
                })
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MEDICAL_REPORT_DATES_BY_PATIENT, {patientId}]
                })
                void queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MEDICAL_REPORT_TYPES_BY_PATIENT, {patientId}]
                })
                toast.dismiss('generate-medical-report-pdf-loading')
                toast.success(t('medical_records:generation_pdf_success'))
                openDetailsModal()
            },
            onError: () => {
                toast.dismiss('generate-medical-report-pdf-loading')
                toast.error(t('errors:default'))
            }
        }
    )

    const isEditable = useMemo(
        () =>
            medicalReport.status == 'draft' ||
            (medicalReport.status == 'signed' &&
                medicalReport.date &&
                dayjs().diff(dayjs(medicalReport.date), 'days') <= 30),
        [medicalReport]
    )

    const canRegeneratePdf = useMemo(() => {
        if (!medicalReport?.pdfGenerationStartedAt && medicalReport.status == 'signed') {
            return true
        }

        return hasPassed5Minutes(medicalReport.pdfGenerationStartedAt) && !medicalReport.pdfAttachment?.url
    }, [medicalReport])

    return (
        <>
            <MenuProvider open={isOpen} setOpen={setOpen}>
                <MenuButton
                    render={
                        <Button
                            className={isOpen ? undefined : 'show-on-hover'}
                            size="xs"
                            shape="square"
                            variant="secondary"
                            onClick={event => event.stopPropagation()}
                        >
                            <DotsHorizontalIcon />
                        </Button>
                    }
                />
                <Menu width="220px" onClick={event => event.stopPropagation()}>
                    <MenuGroup>
                        {isEditable && (
                            <MenuItem
                                render={
                                    <Link
                                        to={generatePath(routes.EDIT_MEDICAL_REPORT.path, {
                                            id: patientId,
                                            medicalReportId: medicalReport.id
                                        })}
                                    >
                                        <PencilLineIcon />
                                        {t('commons:edit')}
                                    </Link>
                                }
                            />
                        )}
                        {canRegeneratePdf ? (
                            <MenuItem
                                render={
                                    <Button
                                        variant="ghost"
                                        disabled={isPending}
                                        onClick={() => generateMedicalReportPdf()}
                                    >
                                        <RefreshCw05Icon />
                                        {t('commons:generate_pdf')}
                                    </Button>
                                }
                            />
                        ) : (
                            <MenuItem
                                disabled={!medicalReport.pdfAttachment?.url}
                                render={
                                    <Link
                                        to={medicalReport.pdfAttachment?.url ?? ''}
                                        target="_blank"
                                        download={true}
                                        rel="noopener noreferrer"
                                    >
                                        <Download01Icon />
                                        {t('commons:export_pdf')}
                                    </Link>
                                }
                            />
                        )}
                    </MenuGroup>
                    <MenuGroup>
                        <MenuItem
                            onClick={() => setIsDeleteReportModalOpen(true)}
                            disabled={medicalReport.status == 'signed'}
                            render={
                                <Button variant="ghostDanger" onClick={() => setIsDeleteReportModalOpen(true)}>
                                    <Trash01Icon />
                                    {t('commons:delete')}
                                </Button>
                            }
                        />
                    </MenuGroup>
                </Menu>
            </MenuProvider>

            {isDeleteReportModalOpen && (
                <DeleteMedicalReportModal
                    medicalReport={medicalReport}
                    onClose={() => setIsDeleteReportModalOpen(false)}
                />
            )}
        </>
    )
}
